/**
 * @module UploadReceiptModalParts
 * @purpose Sub-components for UploadReceiptModal: header, fields, progress, error states
 * @depends CollectionTarget, TFunction, lucide-react
 * @dependedOnBy UploadReceiptModal
 *
 * @rules
 * - Performance: pure presentational components, no data-fetching
 * - State: all state passed via props from parent
 * - Errors: role="alert" on error containers; aria-live on progress
 */

import type { ReactElement, ChangeEvent } from 'react'
import type { TFunction } from 'i18next'
import { X, AlertCircle, CheckCircle, Upload } from 'lucide-react'
import type { CollectionTarget } from '../../../types/collection-targets'
import { getUploadGateState, UploadGateStatus } from '../../collections/UploadGateStatus'
import type { UploadStep } from './use-upload-receipt-modal'

const INPUT_BASE =
  'w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#4e5e82]/30 focus:border-[#4e5e82]'

interface ModalHeaderProps {
  onClose: () => void
  t: TFunction<'cases'>
}

export function ModalHeader({ onClose, t }: ModalHeaderProps): ReactElement {
  return (
    <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {t('detail.collections.uploadReceipt.title')}
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t('detail.collections.uploadReceipt.subtitle')}
        </p>
      </div>
      <button
        onClick={onClose}
        className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
        aria-label={t('actions.close', { ns: 'common' })}
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

interface TargetPickerProps {
  targets: CollectionTarget[]
  target: CollectionTarget | null
  loading: boolean
  showAll: boolean
  onSelect: (target: CollectionTarget | null) => void
  onToggleShowAll: () => void
  t: TFunction<'cases'>
}

export function TargetPicker(props: TargetPickerProps): ReactElement {
  const { targets, target, loading, showAll, onSelect, onToggleShowAll, t } = props
  const selectedId = target?.id ?? ''

  const handleChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    const found = targets.find((tgt) => tgt.id === e.target.value) ?? null
    onSelect(found)
  }

  return (
    <div>
      <label
        htmlFor="upload-target"
        className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
      >
        {t('detail.collections.uploadReceipt.targetLabel')}
      </label>
      <select
        id="upload-target"
        value={selectedId}
        onChange={handleChange}
        disabled={loading}
        className={INPUT_BASE}
      >
        <option value="">{t('detail.collections.uploadReceipt.targetPlaceholder')}</option>
        {targets.map((tgt) => (
          <option key={tgt.id} value={tgt.id} disabled={getUploadGateState(tgt) === 'gated'}>
            {tgt.endpointHostname} ({tgt.targetStatus})
          </option>
        ))}
      </select>
      {target && (
        <div className="mt-1">
          <UploadGateStatus target={target} t={t} />
        </div>
      )}
      <button
        type="button"
        onClick={onToggleShowAll}
        className="mt-1 text-xs text-[#4e5e82] hover:underline dark:text-[#8a9bba]"
      >
        {showAll
          ? t('detail.collections.uploadReceipt.hideFiltered')
          : t('detail.collections.uploadReceipt.showAll')}
      </button>
    </div>
  )
}

interface FilePickerProps {
  id: string
  label: string
  accept: string
  file: File | null
  onChange: (file: File | null) => void
}

export function FilePicker({ id, label, accept, file, onChange }: FilePickerProps): ReactElement {
  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onChange(e.target.files?.[0] ?? null)
  }

  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
      >
        {label}
      </label>
      <input
        id={id}
        type="file"
        accept={accept}
        onChange={handleChange}
        className="block w-full cursor-pointer text-sm text-slate-500 file:mr-3 file:rounded-lg file:border-0 file:bg-[#4e5e82]/10 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-[#4e5e82] hover:file:bg-[#4e5e82]/20 dark:text-slate-400 dark:file:text-[#8a9bba]"
      />
      {file && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{file.name}</p>}
    </div>
  )
}

interface TextFieldProps {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
}

export function TextField({
  id,
  label,
  value,
  onChange,
  placeholder,
}: TextFieldProps): ReactElement {
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
      >
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        placeholder={placeholder}
        className={INPUT_BASE}
      />
    </div>
  )
}

interface ProgressBarProps {
  percent: number
  t: TFunction<'cases'>
}

export function ProgressBar({ percent, t }: ProgressBarProps): ReactElement {
  return (
    <div aria-live="polite" className="space-y-2">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        {t('detail.collections.uploadReceipt.uploading', { percent })}
      </p>
      <div className="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-700">
        <div
          className="h-2 rounded-full bg-lime-500 transition-all duration-300"
          style={{ width: `${percent.toString()}%` }}
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  )
}

interface InlineErrorProps {
  message: string
  step: UploadStep
  onRetry?: () => void
  t: TFunction<'cases'>
}

export function InlineError({ message, step, onRetry, t }: InlineErrorProps): ReactElement {
  return (
    <div
      className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/50"
      role="alert"
      aria-live="polite"
    >
      <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
      <div className="flex-1">
        <p className="text-sm text-red-700 dark:text-red-300">{message}</p>
        {step === 'error' && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 text-xs font-medium text-red-700 hover:underline dark:text-red-300"
          >
            {t('detail.collections.uploadReceipt.retry')}
          </button>
        )}
      </div>
    </div>
  )
}

export function SuccessMessage({ t }: { t: TFunction<'cases'> }): ReactElement {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900 dark:bg-emerald-950/50">
      <CheckCircle className="h-5 w-5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
      <p className="text-sm text-emerald-700 dark:text-emerald-300">
        {t('detail.collections.uploadReceipt.uploadComplete')}
      </p>
    </div>
  )
}

interface FormActionsProps {
  uploading: boolean
  gated: boolean
  onClose: () => void
  t: TFunction<'cases'>
}

export function FormActions({ uploading, gated, onClose, t }: FormActionsProps): ReactElement {
  const uploadDisabled = uploading || gated
  return (
    <div className="flex gap-3 pt-2">
      <button
        type="button"
        onClick={onClose}
        disabled={uploading}
        className="flex-1 rounded-lg border border-slate-200 px-4 py-2 font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
      >
        {t('detail.collections.uploadReceipt.cancel')}
      </button>
      <button
        type="submit"
        disabled={uploadDisabled}
        title={gated ? t('detail.collections.uploadReceipt.receiveFirstWarning') : undefined}
        className="flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 font-medium text-white transition-colors disabled:opacity-50"
        style={{ background: 'var(--accent, #4e5e82)' }}
      >
        {uploading ? (
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
        ) : (
          <Upload className="h-4 w-4" />
        )}
        {t('detail.collections.uploadReceipt.upload')}
      </button>
    </div>
  )
}
